import React, { useState } from 'react'
import Logo from 'js/components/Logo'
import { useDispatch, useSelector } from 'react-redux'
import { logOut, selectIsLoggedIn } from 'js/store/userSlice'
import { Link, useNavigate } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import LanguageSwitcher from 'js/components/Header/LanguageSwitcher'
import type { LanguageCodes } from 'js/locale'
import LoginOverlay from 'js/components/Login/LoginOverlay'
import cn from 'classnames'
import { localizeLink } from 'js/utils'
import useLocale from 'js/hooks/useLocale'

type NavigationProps = {
  customLogo?: React.ReactNode
  theme?: 'light' | 'dark' | 'sub'
  minimal?: boolean
  availableLanguages?: LanguageCodes[]
}

export function Navigation({
  customLogo,
  theme = 'dark',
  minimal = true,
  availableLanguages,
}: NavigationProps = {}) {
  const [showLogin, setShowLogin] = useState(false)
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const navSectionClasses = 'flex items-center md:w-1/3'
  const { language } = useLocale()

  // Theme-based classes
  const isLight = theme === 'light' || theme === 'sub'
  const navBgClass = theme === 'sub' ? 'bg-BgSub' : ''
  const navTextClass = isLight ? 'text-black' : 'text-white'
  const navigationItemClasses = isLight
    ? 'text-black active:text-black focus:text-black hover:text-black transition inline-block ml-12 lg:ml-20 lg:mr-20'
    : 'text-white active:text-LSWhiteLinkActive focus:text-LSWhiteLinkHover hover:text-LSWhiteLinkHover transition inline-block ml-12 lg:ml-20 lg:mr-20'
  const linkNavigationItemClasses = isLight
    ? `lg:type-20 type-[17px] ${navigationItemClasses} border-b-1 border-black/0 hover:border-black/100`
    : `lg:type-20 type-[17px] ${navigationItemClasses} border-b-1 border-white/0 hover:border-LSWhiteLinkHover/100`

  const AccountLink = () => {
    return (
      <Link
        data-uid="account_header_button"
        to="/account/"
        className={linkNavigationItemClasses}
      >
        <FormattedMessage
          id="Header.menuBar.account"
          defaultMessage="Account"
        />
      </Link>
    )
  }

  const LogOutButton = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    return (
      <button
        data-uid="logout_header_button"
        onClick={(e) => {
          e.preventDefault()
          dispatch(logOut())
          navigate('/')
        }}
        className={linkNavigationItemClasses}
      >
        <FormattedMessage id="Header.menuBar.logOut" defaultMessage="Log out" />
      </button>
    )
  }

  const Download = () => (
    <a
      href={localizeLink('/signup/', language)}
      className={cn(
        'rounded-8 border-1 px-20 py-8 font-demibold uppercase leading-[1] type-14',
        navigationItemClasses,
      )}
    >
      <div style={{ transform: 'translateY(1px)' }}>
        <FormattedMessage id="navigation_download" />
      </div>
    </a>
  )

  return (
    <>
      <nav className={cn('px-16 lg:px-12', navTextClass, navBgClass)}>
        <ul className="flex items-center">
          {!minimal && (
            <div
              className={cn(
                navSectionClasses,
                'xs:justify-between md:justify-start',
              )}
            >
              <li className="hidden grow-0 md:inline">
                <Link
                  className={linkNavigationItemClasses}
                  to="/nutrition-explained"
                >
                  <FormattedMessage id="navigation_articles" />
                </Link>
              </li>
              <li className="hidden grow-0 md:inline">
                <Link className={linkNavigationItemClasses} to="/work">
                  <FormattedMessage id="navigation_for_work" />
                </Link>
              </li>
            </div>
          )}
          <li
            className={cn(
              'grow',
              minimal
                ? 'text-center'
                : 'order-first md:order-none md:text-center',
            )}
          >
            {customLogo || (
              <Logo className={isLight ? 'text-black' : 'text-white'} />
            )}
          </li>
          {!minimal && (
            <div className={cn(navSectionClasses, 'justify-end')}>
              <li className="grow-0 md:inline">
                <LanguageSwitcher
                  isLight={isLight}
                  inverted={!isLight}
                  languages={availableLanguages}
                />
              </li>
              {isLoggedIn ? (
                <>
                  <li className="grow-0 sm:order-first md:order-none md:inline">
                    <AccountLink />
                  </li>
                  <li className="grow-0 sm:order-first md:order-none md:inline">
                    <LogOutButton />
                  </li>
                </>
              ) : (
                <li className="grow-0 sm:order-first md:order-none md:inline">
                  <button
                    type="button"
                    data-uid="login_header_button"
                    onClick={() => setShowLogin(true)}
                    className={cn('cursor-pointer', linkNavigationItemClasses)}
                  >
                    <FormattedMessage
                      id="Header.menuBar.logIn"
                      defaultMessage="Log in"
                    />
                  </button>
                </li>
              )}
              <li className="hidden grow-0 md:inline">
                <Download />
              </li>
            </div>
          )}
        </ul>
      </nav>
      {showLogin && (
        <LoginOverlay goToAccount={true} onClose={() => setShowLogin(false)} />
      )}
    </>
  )
}
